import { css } from 'aphrodite';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { Observer, inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { animated, useSpring } from '@react-spring/web';

const AnimatedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const style = useSpring({ from: { opacity: 0 }, to: { opacity: 1 } });
	return <animated.div style={style}>{children}</animated.div>;
};
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../models/AppState';
import { getDisplayName } from '../../../models/NameUtils';
import { IAccount, NoteViewModel } from '../../../viewmodels/AppViewModels';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { FeedCardNoteContent } from '../../../web/components/cards/FeedCardNoteContent';
import { MeetingAttendeesHeader } from '../../../web/components/notes/MeetingAttendeesHeader';
import { baseStyleSheet } from '../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps {
	account: IAccount;
	summaryNote?: NoteViewModel;
	csmNote?: NoteViewModel;
}

class _AccountSummaryHoverNote extends React.Component<IProps> {
	private mCsmNote: NoteViewModel;
	private mLoadingPromise: Promise<void>;
	private mSummaryNote: NoteViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<_AccountSummaryHoverNote, 'mCsmNote' | 'mLoadingPromise' | 'mSummaryNote' | 'loadNotes'>(this, {
			mCsmNote: observable.ref,
			mLoadingPromise: observable.ref,
			mSummaryNote: observable.ref,
			loadNotes: action,
		});

		this.mSummaryNote =
			props?.summaryNote ??
			(props.account?.additionalInfo?.summaryNoteId
				? new NoteViewModel(props.userSession, {
						id: props.account.additionalInfo.summaryNoteId,
					})
				: null);
		this.mCsmNote =
			props?.csmNote ??
			(props.account?.additionalInfo?.csmNoteId
				? new NoteViewModel(props.userSession, {
						id: props.account.additionalInfo.csmNoteId,
					})
				: null);
	}

	public componentDidMount() {
		this.loadNotes();
	}

	public render() {
		return (
			<div className={css(styleSheet.container)}>
				<Observer>
					{() => {
						return this.mLoadingPromise ? (
							<LoadingSpinner type='small' />
						) : !this.mSummaryNote && !this.mCsmNote ? (
							<span>No notes associated with this account yet...</span>
						) : (
							<AnimatedContent>
								{this.renderNote(this.mSummaryNote, 'Summary Note:')}
								{this.renderNote(this.mCsmNote, 'Latest Note:')}
							</AnimatedContent>
						);
					}}
				</Observer>
			</div>
		);
	}

	private renderNote(note: NoteViewModel, title: string) {
		if (!note) {
			return null;
		}
		return (
			<div className={css(styleSheet.note)} key={title}>
				<div className={css(styleSheet.noteTitle)}>{title}</div>
				<div className={css(baseStyleSheet.horizontalStack, styleSheet.noteInfo)}>
					<span>{getDisplayName(note.creator)}</span>
					<span>{moment(note.lastModifiedDateAsDate).format('MMM DD, YYYY hh:mm')}</span>
				</div>
				<MeetingAttendeesHeader note={note} styles={[styleSheet.meetingContext]} />
				<FeedCardNoteContent mountExpanded={true} note={note} onRenderExpansionToggle={this.onRenderExpansionToggle} />
			</div>
		);
	}

	private onRenderExpansionToggle = (): React.ReactNode => {
		return null;
	};

	private loadNotes = () => {
		if (!this.mLoadingPromise) {
			this.mLoadingPromise = new Promise<void>(resolve => {
				const wrappedPromises: Promise<any>[] = [];
				const notes: NoteViewModel[] = [];
				[this.mSummaryNote, this.mCsmNote].forEach(x => {
					if (!!x && !x.rawContentState) {
						wrappedPromises.push(x.load());
						notes.push(x);
					}
				});

				if (wrappedPromises.length === 0) {
					this.mLoadingPromise = null;
					resolve(undefined);
					return;
				}

				const doneCheckCollection = wrappedPromises.map(() => false);
				const doneCheck = (index: number) => {
					doneCheckCollection[index] = true;
					if (doneCheckCollection.every(y => !!y)) {
						runInAction(() => {
							this.mLoadingPromise = null;
							resolve();
						});
					}
				};
				wrappedPromises.forEach((x, i) => {
					x.then(() => doneCheck(i)).catch(() => {
						if (notes[i] === this.mSummaryNote) {
							this.mSummaryNote = null;
						}
						if (notes[i] === this.mCsmNote) {
							this.mCsmNote = null;
						}

						doneCheck(i);
					});
				});
			});
			const onFinish = () => {
				this.mLoadingPromise = null;
			};
			this.mLoadingPromise.then(onFinish).catch(onFinish);
		}
		return this.mLoadingPromise;
	};
}

export const AccountSummaryHoverNote = inject(UserSessionViewModelKey)(observer(_AccountSummaryHoverNote));
